/**
 * uiPanelsStore - one owner for the "which UI panel is open?" flags.
 *
 * The store is authoritative. Panels subscribe to their own flag so resets
 * initiated by lifecycle code synchronously run the same close cleanup as a
 * user action.
 *
 * `missions` is registered here (missionPanel.js used to write its global by
 * hand) but is deliberately EXCLUDED from isUiBlocked() for behavior parity
 * with the pre-store code - flipping that inclusion is its own change with
 * its own scene-input check, not a side effect of this refactor.
 */
import { createAppStore } from './createAppStore';

/** The panels that can block scene input, plus the passive ones we track. */
const PANEL_NAMES = [
  'menu',
  'chat',
  'help',
  'editor',
  'breakouts',
  'poll',
  'qa',
  'missions',
] as const;

export type PanelName = (typeof PANEL_NAMES)[number];

/** Panels whose being open blocks scene input (help + missions are passive). */
const BLOCKING_PANELS: readonly PanelName[] = ['menu', 'chat', 'editor', 'breakouts', 'poll', 'qa'];

export interface UiPanelsState {
  open: Record<PanelName, boolean>;
}

function initialUiPanelsState(): UiPanelsState {
  return {
    open: Object.fromEntries(PANEL_NAMES.map((name) => [name, false])) as Record<
      PanelName,
      boolean
    >,
  };
}

export const uiPanelsStore = createAppStore<UiPanelsState>(initialUiPanelsState());

/**
 * Mark a panel open/closed.
 */
export function setPanelOpen(name: PanelName, open: boolean): void {
  if (!PANEL_NAMES.includes(name)) return;
  const next = !!open;
  if (uiPanelsStore.getState().open[name] === next) return;
  uiPanelsStore.setState((state) => ({ open: { ...state.open, [name]: next } }));
}

export function isPanelOpen(name: PanelName): boolean {
  return !!uiPanelsStore.getState().open[name];
}

/**
 * Is any scene-blocking UI open? (The set interactionManager and the drone
 * hotkeys care about; Help and Missions deliberately excluded - passive cards
 * that should not freeze the world.)
 */
export function isUiBlocked(): boolean {
  const open = uiPanelsStore.getState().open;
  return BLOCKING_PANELS.some((name) => open[name]);
}

export function resetUiPanels(): void {
  uiPanelsStore.setState(initialUiPanelsState(), true);
}
